import React from 'react'
import _ from 'lodash'
import { coreUtils, Icon, coreConstants } from '@adc/ui-morpheus'
import { NONE } from '../constants/optionLists'

const { ColorNames } = coreConstants
const { currency } = coreUtils

const mapAccessRoleCodeToDisplayName = (roles, rolesLookup) =>
  _.map(
    roles,
    role =>
      (_.find(rolesLookup, { role_code: role }) || {}).role_display_name ||
      'Undefined'
  )

const mapPartyRoleCodeToDisplayName = (roles, rolesLookup) =>
  _.map(
    roles,
    role =>
      (_.find(rolesLookup, { code: role.role_name.toUpperCase() }) || {})
        .display_name || 'Undefined'
  )

const mapPhonesToDisplayNumberAndUsage = (phones = []) => (
  <span>
    {phones.map((phone, index) => (
      <span key={index}>
        <span>
          {`${phone.phone_usage} : ${coreUtils.formatters.formatPhoneNumber(
            phone.number
          )}`}
          {phone.verified ? (
            <Icon
              name='check'
              color={ColorNames.Success}
              iconStyle='solid'
              style={{ verticalAlign: 'super', paddingLeft: 2 }}
            />
          ) : undefined}
        </span>
        {index < phones.length - 1 && <span>, </span>}
      </span>
    ))}
  </span>
)

const apiToUiDataRowsTranslation = (
  dataRows,
  accessRolesLookup,
  partyRolesLookup
) => {
  if (!dataRows) return []
  return dataRows.map(row => {
    let newRow = _.cloneDeep(row)

    newRow = {
      ...newRow,
      roles: mapAccessRoleCodeToDisplayName(
        _.get(newRow, 'security.roles', []),
        accessRolesLookup
      ),
      role_name: mapPartyRoleCodeToDisplayName(newRow.roles, partyRolesLookup),
      phones: {
        number: mapPhonesToDisplayNumberAndUsage(newRow.phones),
      },
    }

    return newRow
  })
}

const apiToUiDataRowTranslation = row => apiToUiDataRowsTranslation([row])[0]
const jumioToDatepicker = str =>
  str.replace(/(\d\d\d\d)-(\d\d)-(\d\d)/g, '$2/$3/$1')
const datepickerToJumio = str =>
  str.replace(/(\d\d)\/(\d\d)\/(\d\d\d\d)/g, '$3-$1-$2')

const apiToUi = values => {
  const { addresses: address, ...restApiValues } = values
  const uiValues = {
    address: address ? address[0] : undefined,
    ...restApiValues,
  }

  if (uiValues.address) {
    uiValues.address.user_address_suggestion = {
      ...uiValues.address,
      selectedAddressText: _.trim(
        `${uiValues.address.street_number || ''} ${
          uiValues.address.street_name || ''
        }`
      ),
    }
  }

  uiValues.mobile_phone = _.find(uiValues.phones, { phone_usage: 'Mobile' })
  uiValues.home_phone = _.find(uiValues.phones, { phone_usage: 'Home' })
  uiValues.office_phone = _.find(uiValues.phones, { phone_usage: 'Office' })
  if (uiValues.verification_status)
    uiValues.verification_status.birth_date = jumioToDatepicker(
      _.get(uiValues, 'verification_status.birth_date', '')
    )

  // note some old codes in the data are not uppercase so we'll upper them here
  uiValues.party_roles = (uiValues.roles || []).map(role =>
    role.role_name.toUpperCase()
  )

  if (uiValues.max_bid_settings) {
    uiValues.max_bid_settings.max_bid_fund_amount = currency.stringify(
      uiValues.max_bid_settings.max_bid_fund_amount
    )
    if (!uiValues.max_bid_settings.max_bid_fund_status) {
      uiValues.max_bid_settings.max_bid_fund_status = NONE
    }
  }

  if (uiValues.restrictions) {
    uiValues.restrictions = uiValues.restrictions
      .flatMap(restriction => {
        if (restriction?.deny_conditions) {
          const restriction_conditions = restriction.deny_conditions.flatMap(
            condition => {
              return condition.type === 'PRODUCT_TYPE' && condition.values
            }
          )
          return restriction_conditions
        } else {
          return null
        }
      })
      .filter(
        (product_condition, index, array) =>
          Boolean(product_condition) &&
          array.indexOf(product_condition) === index
      )
  }

  return uiValues
}

const phonesApiFormat = apiValues => {
  const newApiValues = apiValues
  const phoneUsages = ['mobile_phone', 'home_phone', 'office_phone']
  const phones = newApiValues.phones

  phoneUsages.forEach(phoneUsage => {
    if (newApiValues[phoneUsage]) {
      const number = newApiValues[phoneUsage].number.replace(/[^0-9]/g, '')
      const parsedPhoneUsage = _.capitalize(phoneUsage.split('_')[0])
      const index = _.findIndex(phones, { phone_usage: parsedPhoneUsage })

      if (index !== -1) {
        phones.splice(index, 1, { ...phones[index], number })
      } else {
        phones.push({ number, phone_usage: parsedPhoneUsage })
      }
    }
  })

  return phones
}

const maxBiddingApiFormat = maxBidSettings => ({
  max_bid_fund_status:
    maxBidSettings.max_bid_fund_status === NONE
      ? ''
      : maxBidSettings.max_bid_fund_status,
  max_bid_fund_amount: currency.parse(maxBidSettings.max_bid_fund_amount) || 0,
  party_max_bid_id: maxBidSettings.party_max_bid_id,
})

const uiToApi = (values, initialValues = {}) => {
  const apiObj = values
  const hasAddress = !!_.get(
    apiObj,
    'address.user_address_suggestion.selectedAddressText',
    ''
  ).length
  const apiPhones = phonesApiFormat(apiObj)
  const address = _.get(apiObj, 'address', {})

  const addresses = _.merge(
    _.pick(address, [
      'street_unit',
      'address_id',
      'address_usage',
      'coordinates',
      'country_code',
      'country_subdivision',
      'country_primary_subdivision',
      'country_secondary_subdivision',
      'municipality',
      'postal_code',
    ]),
    address.user_address_suggestion
  )

  const verification_status = _.pick(apiObj.verification_status, [
    'party_id',
    'error_message',
    'total_verification_attempts',
    'internal_verification_status',
    'internal_comments',
    'legal_first_name',
    'legal_last_name',
    'birth_date',
    'external_reference_id',
  ])

  if (verification_status.birth_date)
    verification_status.birth_date = datepickerToJumio(
      _.get(verification_status, 'birth_date', '')
    )

  const maxBidSettings = maxBiddingApiFormat(apiObj.max_bid_settings)

  const apiValues = {
    addresses: hasAddress ? [addresses] : null,
    phones: apiPhones,
    ...apiObj,
    verification_status,
    max_bid_settings: maxBidSettings,
    roles: apiObj.party_roles.map(role => ({ role_name: role })), // TO DO - might not want this if we store with other endpoint
  }

  // Pick strictly api values needed to avoid polluting request
  const data = _.pick(apiValues, [
    'addresses',
    'broker_licenses',
    'external_identifiers',
    'is_agent',
    'max_bid_settings',
    'media',
    'person',
    'phones',
    'preferences',
    'relationships',
    'roles', // TO DO - might not want this if we store with other endpoint
    'security',
    'user_id',
    'web_communications',
    'verification_status',
    'restrictions',
    'party_bid_deposit',
  ])

  if (data.addresses && data.addresses[0])
    delete data.addresses[0].selectedAddressText

  return data
}

export {
  apiToUiDataRowTranslation,
  apiToUiDataRowsTranslation,
  apiToUi,
  uiToApi,
}
